import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import './TrophyHall.css';
import {
  Container,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import FilterListIcon from '@mui/icons-material/FilterList';
import TheaterComedyIcon from '@mui/icons-material/TheaterComedy';
import { useAuth } from '../../context/AuthContext';
import { getAllAccolades, ACCOLADE_CATEGORIES, ACCOLADE_RARITIES } from '../../services/accoladeService';
import CreateAccoladeModal from './CreateAccoladeModal';

const TrophyHall = () => {
  const { firestoreUser } = useAuth();
  const location = useLocation();
  const [accolades, setAccolades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // Filter state
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [rarityFilter, setRarityFilter] = useState('all');

  // Ref for scrolling to specific accolade
  const accoladeRefs = useRef({});

  const isAdmin = firestoreUser && firestoreUser.roles && firestoreUser.roles.admin;

  useEffect(() => {
    fetchAccolades();
  }, []);

  // Scroll to accolade if scrollToId is in location state
  useEffect(() => {
    const scrollToId = location.state?.scrollToId;
    if (scrollToId && accoladeRefs.current[scrollToId]) {
      setTimeout(() => {
        accoladeRefs.current[scrollToId]?.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
        // Add a highlight effect
        accoladeRefs.current[scrollToId]?.classList.add('highlight-accolade');
        setTimeout(() => {
          accoladeRefs.current[scrollToId]?.classList.remove('highlight-accolade');
        }, 2000);
      }, 500);
    }
  }, [location.state, accolades]);

  const fetchAccolades = async () => {
    setLoading(true);
    setError('');
    try {
      const accoladesData = await getAllAccolades();
      setAccolades(accoladesData);
    } catch (err) {
      console.error("Error fetching accolades:", err);
      setError('Failed to load accolades. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Date unknown';
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else if (timestamp.seconds) {
      date = new Date(timestamp.seconds * 1000);
    } else {
      date = new Date(timestamp);
    }
    return isNaN(date.getTime()) ? 'Date unknown' : date.toLocaleDateString();
  };

  const getRarityColor = (rarity) => {
    const colors = {
      'Bronze': '#CD7F32',
      'Silver': '#C0C0C0',
      'Gold': '#FFD700',
      'Legendary': '#9C27B0'
    };
    return colors[rarity] || '#666';
  };

  const getRarityIcon = (rarity) => {
    switch (rarity) {
      case 'Bronze': return '🥉';
      case 'Silver': return '🥈';
      case 'Gold': return '🥇';
      case 'Legendary': return '👑';
      default: return '🏆';
    }
  };

  const getCategoryIcon = (category) => {
    const icons = {
      'Combat Excellence': '⚔️',
      'Roleplay': '🎭',
      'Problem Solving': '🧩',
      'Leadership': '👑',
      'Creativity': '🎨',
      'Team Player': '🤝',
      'Character Development': '📈',
      'Memorable Moment': '✨',
      'Strategic Thinking': '🧠',
      'Humor': '😂',
      'Heroic Deed': '🦸',
      'Other': '🏆'
    };
    return icons[category] || '🏆';
  };

  const filteredAccolades = accolades.filter(accolade => {
    const categoryMatch = categoryFilter === 'all' || accolade.category === categoryFilter;
    const rarityMatch = rarityFilter === 'all' || accolade.rarity === rarityFilter;
    
    return categoryMatch && rarityMatch;
  });

  const handleCreateAccolade = () => {
    setShowCreateModal(true);
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleAccoladeCreated = () => {
    setShowCreateModal(false);
    fetchAccolades(); // Refresh the list
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
        <CircularProgress sx={{ color: '#d98d2a' }} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, color: 'white' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EmojiEventsIcon sx={{ fontSize: 40, color: '#d98d2a' }} />
          <Typography variant="h3" component="h1" sx={{ 
            fontFamily: '"Georgia", serif', 
            color: '#d98d2a'
          }}>
            Trophy Hall
          </Typography>
        </Box>
        {isAdmin && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateAccolade}
            sx={{
              backgroundColor: '#d98d2a',
              color: '#1c1c1c',
              fontFamily: '"Georgia", serif',
              '&:hover': { backgroundColor: '#c87d26' }
            }}
          >
            Award Accolade
          </Button>
        )}
      </Box>

      {/* Description */}
      <Typography variant="h6" sx={{ 
        color: '#E0E0E0', 
        fontFamily: '"Georgia", serif',
        mb: 4,
        textAlign: 'center',
        fontStyle: 'italic'
      }}>
        Celebrating the achievements, memorable moments, and outstanding contributions of our adventuring party
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3, '.MuiAlert-message': { color: 'white' }, backgroundColor: '#5c3c10' }}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, backgroundColor: '#3c3c3c', border: '1px solid #555' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <FilterListIcon sx={{ color: '#d98d2a' }} />
          <Typography variant="h6" sx={{ color: '#d98d2a', fontFamily: '"Georgia", serif' }}>
            Filter Accolades
          </Typography>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#d98d2a' }}>Category</InputLabel>
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                label="Category"
                sx={{ 
                  color: '#F5F5F5', 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                  '& .MuiSvgIcon-root': { color: '#d98d2a' }
                }}
              >
                <MenuItem value="all">All Categories</MenuItem>
                {ACCOLADE_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category}>
                    {getCategoryIcon(category)} {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: '#d98d2a' }}>Rarity</InputLabel>
              <Select
                value={rarityFilter}
                onChange={(e) => setRarityFilter(e.target.value)}
                label="Rarity"
                sx={{ 
                  color: '#F5F5F5', 
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#d98d2a' },
                  '& .MuiSvgIcon-root': { color: '#d98d2a' }
                }}
              >
                <MenuItem value="all">All Rarities</MenuItem>
                {ACCOLADE_RARITIES.map((rarity) => (
                  <MenuItem key={rarity} value={rarity}>
                    {getRarityIcon(rarity)} {rarity}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Count */}
      <Typography variant="body1" sx={{ 
        color: '#A9A9A9', 
        fontFamily: '"Georgia", serif',
        mb: 2,
        textAlign: 'center'
      }}>
        Showing {filteredAccolades.length} of {accolades.length} accolades
      </Typography>

      {/* Accolades Grid */}
      {filteredAccolades.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#444', border: '1px solid #d98d2a' }}>
          <EmojiEventsIcon sx={{ fontSize: 64, color: '#666', mb: 2 }} />
          <Typography variant="h6" sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif', mb: 2 }}>
            {accolades.length === 0 ? 'No accolades awarded yet' : 'No accolades match your filters'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#A9A9A9', fontFamily: '"Georgia", serif' }}>
            {accolades.length === 0 
              ? 'Be the first to recognize someone\'s achievements!' 
              : 'Try adjusting your filters to see more accolades.'
            }
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={3}>
          {filteredAccolades.map((accolade) => (
            <Grid item xs={12} md={6} lg={4} key={accolade.id}>
              <Card 
                ref={(el) => accoladeRefs.current[accolade.id] = el}
                sx={{ 
                  backgroundColor: '#3c3c3c', 
                  border: `2px solid ${getRarityColor(accolade.rarity)}`,
                  borderRadius: 2,
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px rgba(217, 141, 42, 0.2)`
                  },
                  '&.highlight-accolade': {
                    animation: 'pulse 1s ease-in-out 2',
                    boxShadow: `0 0 20px ${getRarityColor(accolade.rarity)}`,
                  }
                }}
              >
                {accolade.imageUrl && (
                  <CardMedia
                    component="img"
                    height="200"
                    image={accolade.imageUrl}
                    alt={accolade.title}
                    sx={{ objectFit: 'cover' }}
                  />
                )}
                <CardContent sx={{ p: 3 }}>
                  {/* Header with rarity and category */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip
                      label={accolade.rarity}
                      icon={<span>{getRarityIcon(accolade.rarity)}</span>}
                      sx={{
                        backgroundColor: getRarityColor(accolade.rarity),
                        color: 'white',
                        fontFamily: '"Georgia", serif',
                        fontWeight: 'bold',
                        pointerEvents: 'none'
                      }}
                    />
                    <Chip
                      label={accolade.category}
                      icon={<span>{getCategoryIcon(accolade.category)}</span>}
                      sx={{
                        backgroundColor: '#555',
                        color: '#F5F5F5',
                        fontFamily: '"Georgia", serif',
                        pointerEvents: 'none'
                      }}
                    />
                  </Box>

                  {/* Title */}
                  <Typography variant="h6" sx={{ 
                    color: '#d98d2a', 
                    fontFamily: '"Georgia", serif',
                    fontWeight: 'bold',
                    mb: 1
                  }}>
                    {accolade.title}
                  </Typography>

                  {/* Description */}
                  <Typography variant="body2" sx={{ 
                    color: '#E0E0E0', 
                    fontFamily: '"Georgia", serif',
                    mb: 2,
                    lineHeight: 1.5
                  }}>
                    {accolade.description}
                  </Typography>

                  {/* Character */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <TheaterComedyIcon sx={{ color: '#4caf50' }} />
                    <Typography variant="body2" sx={{ 
                      color: '#F5F5F5', 
                      fontFamily: '"Georgia", serif',
                      fontWeight: 'bold'
                    }}>
                      {accolade.characterName}
                    </Typography>
                  </Box>

                  {/* Episode reference */}
                  {accolade.campaignEpisodeTitle && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" sx={{ 
                        color: '#A9A9A9', 
                        fontFamily: '"Georgia", serif',
                        fontStyle: 'italic'
                      }}>
                        From: {accolade.campaignEpisodeTitle}
                      </Typography>
                    </Box>
                  )}

                  {/* Footer */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 2, borderTop: '1px solid #555' }}>
                    <Typography variant="caption" sx={{ color: '#A9A9A9' }}>
                      Awarded by {accolade.awardedByUserName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: '#A9A9A9' }}>
                      {formatDate(accolade.createdAt)}
                    </Typography>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Accolade Modal */}
      <CreateAccoladeModal
        open={showCreateModal}
        onClose={handleCloseCreateModal}
        onAccoladeCreated={handleAccoladeCreated}
      />
    </Container>
  );
};

export default TrophyHall;
